import type { Column } from "@shared/types/data-table"
import type { Product } from "@shared/types/products"
import type { ColumnManagementConfig } from "@shared/lib/tables/utils/column-management"
import { 
  createTextColumn,
  createStatusColumn, 
  createDateColumn,
  createCustomColumn
} from "./column-factories"
import { formatCurrency } from "@shared/utils/formatters"

export const PRODUCT_COLUMNS: Record<string, Column<Product>> = {
  name: createTextColumn<Product>("name", "Product Name", 250, "font-medium"),
  sku: createTextColumn<Product>("sku", "SKU", 120, "text-muted-foreground font-mono text-xs"),
  category: createTextColumn<Product>("category", "Category", 120, "text-muted-foreground"),
  price: createCustomColumn<Product>(
    "price",
    "Price",
    100,
    (product: Product) => (
      <span className="font-medium">
        {formatCurrency(product.price, product.currency, 2)}
      </span>
    )
  ),
  stock: createCustomColumn<Product>(
    "stock",
    "Stock",
    80,
    (product: Product) => {
      const stock = product.stock || 0
      const stockClass = stock === 0 ? 'text-red-600' : stock <= 10 ? 'text-yellow-600' : 'text-green-600'
      return (
        <span className={stockClass}>
          {stock}
        </span>
      )
    }
  ),
  status: createStatusColumn<Product>("product", "status"),
  created: createDateColumn<Product>("created", "Created"),
}

const PRODUCT_STATUS_CONFIG = {
  all: ["name", "sku", "category", "price", "stock", "status", "created"],
  active: ["name", "sku", "category", "price", "stock", "status", "created"],
  draft: ["name", "category", "price", "status", "created"],
  archived: ["name", "category", "status", "created"],
} as const

export const getProductColumns = (status: string = 'all'): Column<Product>[] => {
  const keys = PRODUCT_STATUS_CONFIG[status as keyof typeof PRODUCT_STATUS_CONFIG] || PRODUCT_STATUS_CONFIG.all
  return keys.map(key => PRODUCT_COLUMNS[key]).filter(Boolean)
}

export const getProductColumnConfig = (status: string = 'all'): ColumnManagementConfig[] => {
  const visibleKeys: Set<string> = new Set(PRODUCT_STATUS_CONFIG[status as keyof typeof PRODUCT_STATUS_CONFIG] || PRODUCT_STATUS_CONFIG.all)
  return Object.entries(PRODUCT_COLUMNS).map(([key, col], index) => ({
    id: col.key,
    label: typeof col.header === 'string' ? col.header : col.key,
    visible: visibleKeys.has(key),
    required: ["name", "status"].includes(key),
    order: index
  }))
}